import { getErrorResponse, handleBlob, preProcessingResponse } from '../helpers'
import {
  APIResponse,
  makeRequest,
  MakeRequestOptions,
  RequestOptions
} from '../make.request'
export type { APIResponse }

type Url = string

export interface ResponseTransformer<T> {
  (json: any): T
}

export interface MakeRequestWrapper<T> {
  body?: BodyInit
  options?: RequestOptions
  url: Url
}

export function mapValues<D, T, R>(data: D, fn: (item: T) => R) {
  return Object.keys(data).reduce(
    (acc, key) => ({ ...acc, [key]: fn(data[key]) }),
    {}
  )
}

const withTransformer =
  <T>(transformer: ResponseTransformer<T>) =>
  (response: T): Promise<T> => {
    const errorResponse = getErrorResponse(response)
    if (errorResponse) {
      return Promise.resolve<T>(response)
    }
    return Promise.resolve<T>(transformer(response))
  }

export const JSONApiResponse = async <T>(
  res: Response,
  transformer: ResponseTransformer<T> = (jsonValue: any) => jsonValue
): Promise<APIResponse<T>> => {
  try {
    return await res
      .json<T>()
      .then<T>(preProcessingResponse)
      .then(withTransformer(transformer))
      .catch(ex => Promise.reject(res))
  } catch (ex) {
    return Promise.reject(res)
  }
}

export const VoidApiResponse = async <T>(
  res: Response
): Promise<APIResponse<T>> => {
  try {
    if (res.status === 204) {
      return Promise.resolve<T>(undefined)
    }
    return await res.json<T>().catch(ex => Promise.reject(res))
  } catch (ex) {
    return Promise.reject(res)
  }
}

export const BlobApiResponse = async <T>(res: Response) => handleBlob(res)

export const TextApiResponse = async <T>(res: Response) => await res.text()

const createInitRequest = <P>({
  url,
  options,
  body
}: MakeRequestWrapper<P>): MakeRequestOptions<P> => {
  const { method } = options

  const common: MakeRequestOptions<P> = {
    useTransactionId: false,
    url,
    method,
    reqOptions: { ...options, signal: null }
  }
  switch (method) {
    case 'HEAD':
    case 'GET':
    case 'DELETE':
      return { ...common, params: body as any as P }
    case 'POST':
    case 'PUT':
    case 'PATCH':
      return {
        ...common,
        reqOptions: { ...common, body: JSON.stringify(body) }
      }
    default:
      throw new Error(`Unsupported HTTP method: ${method}`)
  }
}

export const makeRequestWrapper = <P, T>(
  options: MakeRequestWrapper<P>
): Promise<APIResponse<T>> => makeRequest<P>(createInitRequest<P>(options))
